import { Injectable } from '@angular/core';
import { ICtaAnalyticsData } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-table.interface';
import { ManualFindingStatusStore } from '@cta-analytics-dialog/manual-finding-status.store';
import { EFindingType } from '@enums/campaign-analytics.enum';
import { IFindingVisualizer } from '@interfaces/finding-visualizer.interface';
import { IPendingManualFindingStatus } from '@interfaces/manual-finding-status.interface';

export interface IFindingDeleteContext {
  campaignId: string;
  creatorId: string;
  deliverableId: string;
  socialContentId: string;
}

@Injectable({
  providedIn: 'root',
})
export class FindingDeleteService {
  public buildDeleteMenuItems(
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): Array<{ label: string; findingId: string; keyword: string; keywordId: string }> {
    const allRemovedIds = manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeFindings = element.findings.filter((f) => !allRemovedIds.has(f.id));
    const countByKeyword = new Map<string, number>();

    for (const f of activeFindings) {
      const kw = element.keywordMappings.find((m) => m.keywordId === f.parentId)?.keyword;

      if (kw) countByKeyword.set(kw, (countByKeyword.get(kw) ?? 0) + 1);
    }

    const orderByKeyword = new Map<string, number>();

    return activeFindings.map((finding) => {
      const kw = element.keywordMappings.find((m) => m.keywordId === finding.parentId)?.keyword ?? '';
      const count = countByKeyword.get(kw) ?? 1;
      const order = (orderByKeyword.get(kw) ?? 0) + 1;

      orderByKeyword.set(kw, order);

      const suffix = count > 1 ? ` (${order}.)` : '';

      return { findingId: finding.id, keyword: kw, keywordId: finding.parentId, label: `'${kw}'${suffix}` };
    });
  }

  public getDeleteContext(
    executionAnalytics: ICtaAnalyticsData | null | undefined,
    deliverableId: string | null | undefined,
  ): IFindingDeleteContext | null {
    const campaignId = executionAnalytics?.campaignId;
    const creatorId = executionAnalytics?.creatorId;
    const socialContentId = executionAnalytics?.socialContentId;

    if (!campaignId || !creatorId || !deliverableId || !socialContentId) return null;

    return { campaignId, creatorId, deliverableId, socialContentId };
  }

  public deleteFinding(
    findingId: string,
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
    manualFindingStatusStore: ManualFindingStatusStore,
    context: IFindingDeleteContext,
  ): void {
    const finding = element.findings.find((f) => f.id === findingId);

    if (!finding) return;

    const keyword = element.keywordMappings.find((m) => m.keywordId === finding.parentId)?.keyword ?? '';
    const pending: IPendingManualFindingStatus = {
      campaignId: context.campaignId,
      creatorId: context.creatorId,
      deliverableId: context.deliverableId,
      findingId: finding.id,
      findingType: finding.findingType,
      keyword,
      keywordId: finding.parentId,
      parentId: finding.parentId,
      socialContentId: context.socialContentId,
      visualizerId: element.id,
    };

    manualFindingStatusStore.markForRemoval(pending);
  }

  public deleteAllFindings(
    element: Pick<IFindingVisualizer, 'id' | 'findings' | 'keywordMappings'>,
    manualFindingStatusStore: ManualFindingStatusStore,
    context: IFindingDeleteContext,
  ): void {
    for (const finding of element.findings) {
      if (!manualFindingStatusStore.isHandledFinding(finding.id)) {
        const keyword = element.keywordMappings.find((m) => m.keywordId === finding.parentId)?.keyword ?? '';
        const pending: IPendingManualFindingStatus = {
          campaignId: context.campaignId,
          creatorId: context.creatorId,
          deliverableId: context.deliverableId,
          findingId: finding.id,
          findingType: finding.findingType,
          keyword,
          keywordId: finding.parentId,
          parentId: finding.parentId,
          socialContentId: context.socialContentId,
          visualizerId: element.id,
        };

        manualFindingStatusStore.markForRemoval(pending);
      }
    }
  }

  public deleteSingleRowFinding(
    element: { id: string; findingParentId: string },
    findingType: EFindingType,
    manualFindingStatusStore: ManualFindingStatusStore,
    context: IFindingDeleteContext,
  ): void {
    const pending: IPendingManualFindingStatus = {
      campaignId: context.campaignId,
      creatorId: context.creatorId,
      deliverableId: context.deliverableId,
      findingId: element.id,
      findingType,
      keyword: '',
      keywordId: element.findingParentId,
      parentId: element.findingParentId,
      socialContentId: context.socialContentId,
      visualizerId: element.id,
    };

    manualFindingStatusStore.markForRemoval(pending);
  }

  public revertRow(
    element: Pick<IFindingVisualizer, 'id'>,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): void {
    manualFindingStatusStore?.revertVisualizerDeletions(element.id);
  }

  public hasRowPendingDeletions(
    element: Pick<IFindingVisualizer, 'id'>,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): boolean {
    return (manualFindingStatusStore?.getPendingForVisualizer(element.id).length ?? 0) > 0;
  }

  public hasRowActiveFinding(
    element: Pick<IFindingVisualizer, 'findings'>,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): boolean {
    const allRemovedIds = manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();

    return element.findings.some((f) => !allRemovedIds.has(f.id));
  }

  public getActiveKeywords(
    element: Pick<IFindingVisualizer, 'findings' | 'keywordMappings'>,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): string[] {
    const allRemovedIds = manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeKeywordIds = new Set(element.findings.filter((f) => !allRemovedIds.has(f.id)).map((f) => f.parentId));

    return element.keywordMappings.filter((m) => activeKeywordIds.has(m.keywordId)).map((m) => m.keyword);
  }

  public isVisualizerActing(
    visualizerId: string,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): boolean {
    return manualFindingStatusStore?.isVisualizerActing(visualizerId) ?? false;
  }

  public isFindingInFlight(
    findingId: string,
    manualFindingStatusStore: ManualFindingStatusStore | null | undefined,
  ): boolean {
    return manualFindingStatusStore?.isFindingInFlight(findingId) ?? false;
  }
}
